import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ShieldCheck, ShieldAlert, Copy, Check, KeyRound } from "lucide-react";

export const SecurityDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [setup, setSetup] = useState(null); // {otpauth_uri, qr_data_url}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/auth/me");
      setEnabled(!!data.totp_enabled);
      setRemaining(data.backup_codes_remaining || 0);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) {
      setSetup(null); setCode(""); setBackupCodes(null); setDisableCode("");
      setCurPw(""); setNewPw(""); setConfirmPw("");
      loadStatus();
    }
  }, [open]);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/auth/mfa/enroll");
      setSetup(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start setup"); }
    finally { setBusy(false); }
  };

  const confirmEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/auth/mfa/confirm", { code: code.trim() });
      setBackupCodes(data.backup_codes);
      setEnabled(true);
      setSetup(null);
      toast.success("Two-factor authentication enabled");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invalid code"); }
    finally { setBusy(false); }
  };

  const disable2fa = async () => {
    setBusy(true);
    try {
      await adminApi.post("/auth/mfa/disable", { code: disableCode.trim() });
      setEnabled(false);
      setDisableCode("");
      toast.success("Two-factor authentication disabled");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invalid code"); }
    finally { setBusy(false); }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const changePassword = async () => {
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { toast.error("New password and confirmation do not match"); return; }
    if (newPw === curPw) { toast.error("New password must be different from the current one"); return; }
    setPwBusy(true);
    try {
      await adminApi.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      setCurPw(""); setNewPw(""); setConfirmPw("");
      toast.success("Password updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update password"); }
    finally { setPwBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#F8F6F0] max-w-md" data-testid="security-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            {enabled ? <ShieldCheck className="h-5 w-5 text-[#2D6A4F]" /> : <ShieldAlert className="h-5 w-5 text-[#C85A32]" />}
            Two-factor authentication
          </DialogTitle>
          <DialogDescription>
            Protect your seller account with a time-based code from Google Authenticator.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#C85A32]" /></div>
        ) : backupCodes ? (
          <div className="space-y-3" data-testid="backup-codes-panel">
            <p className="text-sm text-[#5C5852] font-medium">Save these one-time recovery codes somewhere safe. They will not be shown again.</p>
            <div className="grid grid-cols-2 gap-2 bg-white border border-[#E2DEC9] rounded-lg p-4 font-mono text-sm">
              {backupCodes.map((c) => <div key={c} data-testid="backup-code">{c}</div>)}
            </div>
            <Button variant="outline" className="w-full border-[#C8C2AC]" onClick={copyCodes} data-testid="copy-backup-codes">
              {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />} Copy codes
            </Button>
            <Button className="w-full bg-[#161616]" onClick={() => onOpenChange(false)} data-testid="backup-codes-done">Done</Button>
          </div>
        ) : setup ? (
          <div className="space-y-4" data-testid="enroll-panel">
            <p className="text-sm text-[#5C5852]">Scan this QR code with Google Authenticator, then enter the 6-digit code to confirm.</p>
            <div className="flex justify-center">
              <img src={setup.qr_data_url} alt="Scan with Google Authenticator" className="w-44 h-44 rounded-lg border border-[#E2DEC9] bg-white p-2" data-testid="totp-qr" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest text-[#8C867B]">Can't scan? Enter this key</Label>
              <code className="block break-all text-xs bg-white border border-[#E2DEC9] rounded p-2 text-[#5C5852]" data-testid="totp-uri">{setup.otpauth_uri}</code>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#5C5852]">6-digit code</Label>
              <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456" className="h-11 bg-white border-[#C8C2AC] font-mono tracking-[0.3em] text-center" data-testid="totp-confirm-input" />
            </div>
            <Button className="w-full bg-[#C85A32] hover:bg-[#B04B26]" disabled={code.length !== 6 || busy} onClick={confirmEnroll} data-testid="totp-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & enable"}
            </Button>
          </div>
        ) : enabled ? (
          <div className="space-y-4" data-testid="enabled-panel">
            <div className="bg-[#2D6A4F]/10 border border-[#2D6A4F]/25 rounded-lg p-3 text-sm text-[#2D6A4F]">
              2FA is <strong>active</strong>. You'll be asked for a code at every sign in. Recovery codes left: <strong>{remaining}</strong>
            </div>
            <div className="space-y-2 pt-2 border-t border-[#E2DEC9]">
              <Label className="text-xs uppercase tracking-widest text-[#5C5852]">Disable 2FA (enter current code)</Label>
              <Input inputMode="numeric" maxLength={6} value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456" className="h-11 bg-white border-[#C8C2AC] font-mono tracking-[0.3em] text-center" data-testid="totp-disable-input" />
              <Button variant="outline" className="w-full border-[#9E2A2B]/40 text-[#9E2A2B] hover:bg-[#9E2A2B]/10" disabled={disableCode.length !== 6 || busy} onClick={disable2fa} data-testid="totp-disable-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disable two-factor"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="disabled-panel">
            <p className="text-sm text-[#5C5852]">Two-factor authentication is currently <strong>off</strong>. Add an authenticator app for stronger protection.</p>
            <Button className="w-full bg-[#C85A32] hover:bg-[#B04B26]" disabled={busy} onClick={startEnroll} data-testid="start-2fa-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set up 2FA"}
            </Button>
          </div>
        )}

        {!loading && !setup && !backupCodes && (
          <div className="space-y-3 pt-4 mt-2 border-t border-[#E2DEC9]" data-testid="change-password-panel">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-[#5C5852]" />
              <h3 className="font-serif text-lg text-[#1A1918]">Change password</h3>
            </div>
            <Input type="password" placeholder="Current password" value={curPw} onChange={(e) => setCurPw(e.target.value)} className="h-11 bg-white border-[#C8C2AC]" data-testid="current-password-input" />
            <Input type="password" placeholder="New password (min 8 characters)" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="h-11 bg-white border-[#C8C2AC]" data-testid="new-password-input" />
            <Input type="password" placeholder="Confirm new password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="h-11 bg-white border-[#C8C2AC]" data-testid="confirm-password-input" />
            <Button className="w-full bg-[#161616] hover:bg-[#2a2a28]" disabled={pwBusy || !curPw || !newPw || !confirmPw} onClick={changePassword} data-testid="change-password-button">
              {pwBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
